import { isEmpty, isEqual, isNil, join, map, uniq } from "lodash";
import {
  GroupedMerchantAddress,
  MerchantStoreName,
  MerchantStoreSiteId,
} from "../../features/api-interfaces";
import { COMMON_LABELS, DivisionDict } from "../default-constants";
import {
  ADDRESS_OTHER_LABEL,
  ADDRESS_TAX,
  ADDRESS_TSR,
  CONTRACT_COMPREHENSIVE,
  CONTRACT_DIRECTLY,
  CONTRACT_ROUTE_SELECT_ITEMS,
  LABELS,
  RECONCILABLE_PERIOD,
} from "./constants";
import { wrapWithParentheses } from "../../utils/format";

/**
 * 加盟店取得結果をプルダウン用のオブジェクトに変換する.
 * 加盟店の重複は削除する.
 * @param {MerchantStoreName[]} merchants
 * @returns {DivisionDict[]} 変換された加盟店取得結果
 */
export const getMerchantStoreNameItems = (
  merchants: MerchantStoreName[] | undefined
): DivisionDict[] => {
  if (merchants !== undefined) {
    return merchants
      .filter(
        (merchant, index, self) =>
          index === self.findIndex((s) => s.tradeName === merchant.tradeName)
      )
      .map((merchant) => ({
        label: merchant.tradeName,
        value: merchant.tradeName,
      }));
  }
  return [];
};

/**
 * サイトID取得結果をプルダウン用のオブジェクトに変換する.
 * サイトIDの重複は削除する.
 * @param {MerchantStoreSiteId[]} siteIds
 * @returns {DivisionDict[]} 変換されたサイトID取得結果
 */
export const getMerchantStoreSiteIdItems = (
  siteIds: MerchantStoreSiteId[] | undefined
): DivisionDict[] => {
  if (siteIds !== undefined) {
    return siteIds
      .filter(
        (siteId, index, self) =>
          index === self.findIndex((s) => s.cpid === siteId.cpid)
      )
      .map((siteId) => ({
        label: siteId.cpid,
        value: siteId.cpid,
      }));
  }
  return [];
};

/**
 * 引数の契約形態のリストを基に契約形態の画面表示用のラベルを返却する
 * @param {number[] | undefined} contractStyles 契約形態のリスト
 * @returns {string} 契約形態の画面表示用のラベル
 */
export const getContractStyleLabel = (contractStyles: number[] | undefined) => {
  const directlyLabel = CONTRACT_ROUTE_SELECT_ITEMS.find(
    (item) => item.value === CONTRACT_DIRECTLY
  )!.label;
  const comprehensiveLabel = CONTRACT_ROUTE_SELECT_ITEMS.find(
    (item) => item.value === CONTRACT_COMPREHENSIVE
  )!.label;

  return contractStyles
    ?.map((contractStyles) => {
      if (contractStyles === Number(CONTRACT_DIRECTLY)) {
        return directlyLabel + LABELS.CONTRACT;
      } else if (contractStyles === Number(CONTRACT_COMPREHENSIVE)) {
        return comprehensiveLabel + LABELS.CONTRACT;
      } else {
        return COMMON_LABELS.BLANK;
      }
    })
    .join(" ");
};

/**
 * 画面表示用の売上高を返却する
 * @param {string | undefined} sales APIから受け取った売上高
 * @param {string | undefined} nowDate APIから受け取った売上高年度
 * @returns {string} 画面表示用の売上高
 */
export const getDisplaySales = (
  sales: string | undefined,
  nowDate: string | undefined
) => {
  let salesStr = "";
  if (sales !== undefined && sales !== null) {
    salesStr += sales + LABELS.BILLION_YEN;
  } else {
    salesStr += LABELS.BLANK_SPACE_ONE;
  }
  if (nowDate !== undefined && nowDate !== null) {
    salesStr +=
      LABELS.BLANK_SPACE_ONE +
      COMMON_LABELS.LEFT_PARENTHESIS +
      nowDate +
      LABELS.YEAR +
      COMMON_LABELS.RIGHT_PARENTHESIS;
  }

  return salesStr.replace(LABELS.BLANK_SPACE_ONE, "").length !== 0
    ? salesStr
    : LABELS.BLANK_SPACE_ONE;
};

/**
 * 突合可能期限ラベルを返却する.
 * 1: 翌々月末, 2: 90日後, 4: 367日
 * @param {string} reconcilablePeriodCode 突合可能期限コード
 * @returns {string} 突合可能期限ラベル
 */
export const getReconcilablePeriodLabel = (reconcilablePeriodCode: string) => {
  const { endOfNextNextMonth, ninetyDaysLater, oneYear } = RECONCILABLE_PERIOD;
  switch (reconcilablePeriodCode) {
    case endOfNextNextMonth.value:
      return endOfNextNextMonth.label;
    case ninetyDaysLater.value:
      return ninetyDaysLater.label;
    case oneYear.value:
      return oneYear.label;
    default:
      return "";
  }
};

/**
 * 受け取った住所をもとに画面表示用の住所に整形して返却する.
 * @param {string | undefined} address1 住所1
 * @param {string | undefined} address2 住所2
 * @returns {string} 住所情報
 */
export const formatAddress = (
  address1: string | undefined,
  address2: string | undefined
): string => {
  // 住所がnullやundefineの場合は空文字を詰める
  const a1 = address1?.trim() ?? "";
  const a2 = address2?.trim() ?? "";

  const address = a1 + a2;

  return address === "" ? COMMON_LABELS.HYPHEN : address;
};

/**
 * 住所詳細ボタンのラベルを取得する.
 * @param {GroupedMerchantAddress[]} address 住所情報
 * @returns 詳細ボタンラベル名
 */
export const getMerchantAddressDetailButtonLabel = (
  address: GroupedMerchantAddress[]
): string => {
  return !isNil(address) && address.length > 1
    ? LABELS.ADDRESS_DETAILS_OTHER
    : LABELS.ADDRESS_DETAILS_NOT_OTHER;
};

/**
 * 住所種別のラベルを返却する.
 *
 * 　1: （TSR）
 * 　2: （国税庁）
 *  それ以外: （その他）
 * @param {number} addressType 対象の種別
 * @returns {string} 確認結果欄に表示する住所種別のラベル
 */
export const getAddressLabel = (addressType: number): string => {
  switch (String(addressType)) {
    case ADDRESS_TSR.value:
      return wrapWithParentheses(ADDRESS_TSR.label);
    case ADDRESS_TAX.value:
      return wrapWithParentheses(ADDRESS_TAX.label);
    default:
      return wrapWithParentheses(ADDRESS_OTHER_LABEL);
  }
};

/**
 * 引数で受け取った住所種別の住所情報を返却する.
 *
 * ただし、住所情報リストに対象の住所種別がない場合はundefinedを返す.
 * @param {GroupedMerchantAddress} addressList 住所情報リスト
 * @param {number} addressType 抽出する住所種別
 * @returns {GroupedMerchantAddress | undefined} 抽出した住所情報
 */
export const getAddress = (
  addressList: GroupedMerchantAddress[] | null | undefined,
  addressType: number
): GroupedMerchantAddress | undefined => {
  const address = addressList?.find((address) =>
    isEqual(address.addressType, addressType)
  );

  return address;
};

/**
 * 契約ルートのリストを半角スラッシュ区切りの文字列に変換して返却する.
 * @param {(string | null | undefined)[]} contractRouteList 契約ルートのリスト
 * @returns {string} 文字列の契約ルート
 */
export const getContractRouteStr = (
  contractRouteList: (string | null | undefined)[]
): string => {
  if (isEmpty(contractRouteList)) {
    // 空の場合は、「直接契約」を返却する
    return LABELS.DIRECT_CONTRACT;
  }

  // 半角スラッシュ区切りの文字列を生成する
  return join(
    uniq(
      map(contractRouteList, (contractRoute) =>
        isEmpty(contractRoute) ? LABELS.DIRECT_CONTRACT : contractRoute
      )
    ),
    COMMON_LABELS.SLASH
  );
};
